import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DISPLAY_OPTIONS = [
  { value: "logo",      label: "Logo only" },
  { value: "icon",      label: "Icon only" },
  { value: "name",      label: "Name only" },
  { value: "icon_name", label: "Icon + Name" },
  { value: "logo_name", label: "Logo + Name" },
];

const EMPTY_FORM = {
  name: "",
  logoData: null,
  iconData: null,
  displayMode: "logo",
  colorMode: "bw",
  isVisible: true,
  displayOrder: 0,
};

/* ── Carousel preview (dark background, mirrors ClientsStrip) ─────────── */
function CarouselPreview({ form }) {
  const { displayMode, colorMode, name, logoData, iconData } = form;
  const imgFilter = colorMode === "bw" ? "brightness(0) invert(1)" : "none";

  const logo = logoData
    ? <img src={logoData} alt={name || "logo"}
        style={{ height: "26px", width: "auto", objectFit: "contain", filter: imgFilter, opacity: 0.85 }} />
    : null;
  const icon = iconData
    ? <img src={iconData} alt=""
        style={{ height: "22px", width: "22px", objectFit: "contain", filter: imgFilter, opacity: 0.85 }} />
    : null;
  const nameEl = name
    ? <span style={{ fontFamily: "Montserrat, sans-serif", fontSize: "13px", fontWeight: 600,
                     color: "rgba(255,255,255,0.8)", letterSpacing: "0.5px" }}>{name}</span>
    : null;

  let content;
  if (displayMode === "logo")      content = logo || nameEl;
  else if (displayMode === "icon") content = icon || nameEl;
  else if (displayMode === "name") content = nameEl;
  else if (displayMode === "icon_name") content = (
    <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>{icon}{nameEl}</div>
  );
  else if (displayMode === "logo_name") content = (
    <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>{logo || icon}{nameEl}</div>
  );

  return (
    <div style={{ marginTop: "20px" }}>
      <div style={{ fontSize: "11px", fontWeight: 600, color: "#6B7280",
                    letterSpacing: "1px", textTransform: "uppercase", marginBottom: "8px" }}>
        Carousel preview
      </div>
      <div style={{ background: "#080C0C", borderRadius: "8px", padding: "18px 28px",
                    display: "inline-flex", alignItems: "center", minWidth: "120px",
                    justifyContent: "center", border: "1px solid #1a2a29" }}>
        {content || (
          <span style={{ color: "rgba(255,255,255,0.18)", fontSize: "12px" }}>Nothing to show</span>
        )}
      </div>
    </div>
  );
}

/* ── Table row preview ─────────────────────────────────────────────────── */
function TablePreview({ client }) {
  const { displayMode = "logo", colorMode = "bw", name, logoData, iconData } = client;
  const imgFilter = colorMode === "bw" ? "brightness(0) invert(1)" : "none";

  const logo = logoData
    ? <img src={logoData} alt="" style={{ height: "20px", width: "auto", objectFit: "contain", filter: imgFilter }} />
    : null;
  const icon = iconData
    ? <img src={iconData} alt="" style={{ height: "18px", width: "18px", objectFit: "contain", filter: imgFilter }} />
    : null;
  const nameEl = name
    ? <span style={{ fontFamily: "Montserrat", fontSize: "11px", fontWeight: 600,
                     color: "#fff", letterSpacing: "0.5px" }}>{name}</span>
    : null;

  let content;
  if (displayMode === "logo")      content = logo || nameEl;
  else if (displayMode === "icon") content = icon || nameEl;
  else if (displayMode === "name") content = nameEl;
  else if (displayMode === "icon_name") content = (
    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>{icon}{nameEl}</div>
  );
  else if (displayMode === "logo_name") content = (
    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>{logo || icon}{nameEl}</div>
  );

  return (
    <div style={{ background: "#0A0A0A", borderRadius: "5px", padding: "7px 12px",
                  display: "inline-flex", alignItems: "center", minWidth: "60px", justifyContent: "center" }}>
      {content || <span style={{ color: "rgba(255,255,255,0.15)", fontSize: "10px" }}>—</span>}
    </div>
  );
}

/* ── Image upload field ────────────────────────────────────────────────── */
function ImageField({ label, hint, value, onChange, onClear }) {
  const ref = useRef();

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onChange(ev.target.result);
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ marginBottom: "18px" }}>
      <label style={{ display: "block", fontSize: "13px", fontWeight: 600,
                      color: "#374151", marginBottom: "6px" }}>{label}</label>
      {hint && <div style={{ fontSize: "11px", color: "#9CA3AF", marginBottom: "8px" }}>{hint}</div>}
      {value ? (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ background: "#0A0A0A", borderRadius: "6px", padding: "8px 14px",
                        border: "1px solid #E5E7EB" }}>
            <img src={value} alt="" style={{ height: "28px", width: "auto", objectFit: "contain",
                                             maxWidth: "120px" }} />
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button type="button" onClick={() => ref.current?.click()}
              style={{ fontSize: "12px", color: "#2D5C5A", background: "none", border: "none",
                       cursor: "pointer", textDecoration: "underline" }}>
              Replace
            </button>
            <button type="button" onClick={onClear}
              style={{ fontSize: "12px", color: "#EF4444", background: "none", border: "none",
                       cursor: "pointer", textDecoration: "underline" }}>
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()}
          style={{ padding: "8px 16px", background: "#F3F4F6", border: "1px dashed #D1D5DB",
                   borderRadius: "6px", fontSize: "13px", color: "#6B7280", cursor: "pointer" }}>
          + Upload image
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
    </div>
  );
}

/* ── Add / Edit modal ──────────────────────────────────────────────────── */
function ClientModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState(client
    ? { name: client.name || "", logoData: client.logoData || null, iconData: client.iconData || null,
        displayMode: client.displayMode || "logo", colorMode: client.colorMode || "bw",
        isVisible: client.isVisible !== false, displayOrder: client.displayOrder || 0 }
    : EMPTY_FORM
  );
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name || null,
        logoData: form.logoData || null,
        iconData: form.iconData || null,
        displayMode: form.displayMode,
        colorMode: form.colorMode,
        isVisible: form.isVisible,
        displayOrder: Number(form.displayOrder) || 0,
      };
      if (client) {
        await axios.put(`${API}/admin/clients/${client.id}`, payload);
      } else {
        await axios.post(`${API}/admin/clients`, payload);
      }
      onSaved();
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "9px 12px", borderRadius: "6px",
    border: "1px solid #D1D5DB", fontSize: "14px", color: "#111",
    fontFamily: "inherit", outline: "none", boxSizing: "border-box",
  };
  const labelStyle = { display: "block", fontSize: "13px", fontWeight: 600,
                        color: "#374151", marginBottom: "6px" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 100,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: "12px", width: "100%", maxWidth: "560px",
                    maxHeight: "90vh", overflowY: "auto", padding: "32px",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                      marginBottom: "24px" }}>
          <h2 style={{ margin: 0, fontFamily: "Montserrat", fontSize: "18px", fontWeight: 700,
                       color: "#111" }}>
            {client ? "Edit Client" : "Add Client"}
          </h2>
          <button onClick={onClose}
            style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer",
                     color: "#9CA3AF", lineHeight: 1 }}>×</button>
        </div>

        {/* Name */}
        <div style={{ marginBottom: "18px" }}>
          <label style={labelStyle}>Brand name <span style={{ color: "#9CA3AF", fontWeight: 400 }}>(optional)</span></label>
          <input style={inputStyle} placeholder="e.g. Vercel" value={form.name}
            onChange={e => set("name", e.target.value)} />
        </div>

        {/* Logo upload */}
        <ImageField
          label="Logo image (optional)"
          hint="Wide format recommended — e.g. 240×60 px. PNG with transparency works best."
          value={form.logoData}
          onChange={v => set("logoData", v)}
          onClear={() => set("logoData", null)}
        />

        {/* Icon upload */}
        <ImageField
          label="Icon image (optional)"
          hint="Square format recommended — e.g. 64×64 px."
          value={form.iconData}
          onChange={v => set("iconData", v)}
          onClear={() => set("iconData", null)}
        />

        {/* Display mode */}
        <div style={{ marginBottom: "18px" }}>
          <label style={labelStyle}>Show in carousel</label>
          <select style={{ ...inputStyle, background: "#fff" }} value={form.displayMode}
            onChange={e => set("displayMode", e.target.value)}>
            {DISPLAY_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Color mode */}
        <div style={{ marginBottom: "18px" }}>
          <label style={labelStyle}>Image style</label>
          <div style={{ display: "flex", gap: "12px" }}>
            {[{ value: "bw", label: "Black & White (white on dark)" },
              { value: "color", label: "Color" }].map(opt => (
              <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: "7px",
                                              fontSize: "13px", color: "#374151", cursor: "pointer" }}>
                <input type="radio" name="colorMode" value={opt.value} checked={form.colorMode === opt.value}
                  onChange={() => set("colorMode", opt.value)} />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        {/* Display order */}
        <div style={{ marginBottom: "18px" }}>
          <label style={labelStyle}>Display order <span style={{ color: "#9CA3AF", fontWeight: 400 }}>(lower = first)</span></label>
          <input style={{ ...inputStyle, width: "120px" }} type="number" min="0"
            value={form.displayOrder} onChange={e => set("displayOrder", e.target.value)} />
        </div>

        {/* Visible toggle */}
        <div style={{ marginBottom: "24px", display: "flex", alignItems: "center", gap: "10px" }}>
          <label style={{ position: "relative", display: "inline-block", width: "40px", height: "22px" }}>
            <input type="checkbox" checked={form.isVisible} onChange={e => set("isVisible", e.target.checked)}
              style={{ opacity: 0, width: 0, height: 0 }} />
            <span style={{
              position: "absolute", cursor: "pointer", inset: 0, borderRadius: "22px",
              background: form.isVisible ? "#2D5C5A" : "#D1D5DB",
              transition: "background 0.2s",
            }}>
              <span style={{
                position: "absolute", top: "3px", left: form.isVisible ? "21px" : "3px",
                width: "16px", height: "16px", borderRadius: "50%", background: "#fff",
                transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              }} />
            </span>
          </label>
          <span style={{ fontSize: "13px", color: "#374151" }}>Show in clients carousel</span>
        </div>

        {/* Live preview */}
        <CarouselPreview form={form} />

        {/* Actions */}
        <div style={{ display: "flex", gap: "12px", marginTop: "28px", justifyContent: "flex-end" }}>
          <button onClick={onClose}
            style={{ padding: "10px 20px", background: "#F3F4F6", border: "1px solid #E5E7EB",
                     borderRadius: "6px", fontSize: "14px", color: "#374151", cursor: "pointer",
                     fontFamily: "inherit" }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: "10px 24px", background: "#2D5C5A", border: "none",
                     borderRadius: "6px", fontSize: "14px", color: "#fff", cursor: "pointer",
                     fontFamily: "inherit", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving…" : client ? "Save changes" : "Add client"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main component ────────────────────────────────────────────────────── */
export default function ClientsAdmin() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // null | "add" | clientObj
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API}/admin/clients`);
      if (res.data?.success) setClients(res.data.data);
    } catch {
      setError("Failed to load clients.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, []);

  const handleToggleVisibility = async (client) => {
    try {
      await axios.patch(`${API}/admin/clients/${client.id}/visibility`);
      fetchClients();
    } catch {
      alert("Failed to update visibility.");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/clients/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchClients();
    } catch {
      alert("Failed to delete client.");
    }
  };

  const th = { padding: "11px 16px", textAlign: "left", fontSize: "11px", fontWeight: 600,
               color: "#6B7280", letterSpacing: "1px", textTransform: "uppercase",
               background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" };
  const td = { padding: "14px 16px", borderBottom: "1px solid #F3F4F6",
               fontSize: "13px", color: "#374151", verticalAlign: "middle" };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                    marginBottom: "28px" }}>
        <div>
          <h1 style={{ fontFamily: "Montserrat", fontSize: "22px", fontWeight: 700,
                       color: "#111", margin: 0 }}>Our Clients</h1>
          <p style={{ fontSize: "13px", color: "#9CA3AF", marginTop: "4px", marginBottom: 0 }}>
            Manage logos shown in the marquee strip on the homepage.
          </p>
        </div>
        <button onClick={() => setModal("add")}
          style={{ padding: "10px 20px", background: "#2D5C5A", color: "#fff", border: "none",
                   borderRadius: "7px", fontFamily: "Montserrat", fontWeight: 600, fontSize: "13px",
                   cursor: "pointer" }}>
          + Add client
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "8px",
                      padding: "12px 16px", color: "#DC2626", fontSize: "13px", marginBottom: "20px" }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: "10px", border: "1px solid #E5E7EB",
                    overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Preview</th>
              <th style={th}>Name</th>
              <th style={th}>Show</th>
              <th style={th}>Style</th>
              <th style={th}>Order</th>
              <th style={th}>Status</th>
              <th style={{ ...th, textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ ...td, textAlign: "center", color: "#9CA3AF", padding: "40px" }}>
                Loading…
              </td></tr>
            ) : clients.length === 0 ? (
              <tr><td colSpan={7} style={{ ...td, textAlign: "center", color: "#9CA3AF", padding: "48px" }}>
                No clients yet. Click <strong>+ Add client</strong> to get started.
              </td></tr>
            ) : clients.map(client => (
              <tr key={client.id} style={{ background: "#fff" }}>
                <td style={td}><TablePreview client={client} /></td>
                <td style={td}>{client.name || <span style={{ color: "#D1D5DB" }}>—</span>}</td>
                <td style={td}>
                  {DISPLAY_OPTIONS.find(o => o.value === client.displayMode)?.label || client.displayMode}
                </td>
                <td style={td}>
                  <span style={{
                    display: "inline-block", padding: "2px 8px", borderRadius: "4px", fontSize: "11px",
                    fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase",
                    background: client.colorMode === "bw" ? "#F3F4F6" : "#ECFDF5",
                    color: client.colorMode === "bw" ? "#374151" : "#065F46",
                  }}>
                    {client.colorMode === "bw" ? "B&W" : "Color"}
                  </span>
                </td>
                <td style={td}>{client.displayOrder}</td>
                <td style={td}>
                  <span style={{
                    display: "inline-block", padding: "3px 10px", borderRadius: "20px", fontSize: "11px",
                    fontWeight: 600, background: client.isVisible ? "#D1FAE5" : "#F3F4F6",
                    color: client.isVisible ? "#065F46" : "#6B7280",
                  }}>
                    {client.isVisible ? "Visible" : "Hidden"}
                  </span>
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                    <button onClick={() => setModal(client)}
                      style={{ padding: "5px 12px", background: "#EFF6FF", border: "1px solid #BFDBFE",
                               borderRadius: "5px", fontSize: "12px", color: "#1D4ED8", cursor: "pointer" }}>
                      Edit
                    </button>
                    <button onClick={() => handleToggleVisibility(client)}
                      style={{ padding: "5px 12px", background: client.isVisible ? "#FEF3C7" : "#F0FDF4",
                               border: `1px solid ${client.isVisible ? "#FDE68A" : "#BBF7D0"}`,
                               borderRadius: "5px", fontSize: "12px",
                               color: client.isVisible ? "#92400E" : "#166534", cursor: "pointer" }}>
                      {client.isVisible ? "Hide" : "Show"}
                    </button>
                    <button onClick={() => setDeleteTarget(client)}
                      style={{ padding: "5px 12px", background: "#FEF2F2", border: "1px solid #FECACA",
                               borderRadius: "5px", fontSize: "12px", color: "#DC2626", cursor: "pointer" }}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add / Edit modal */}
      {modal !== null && (
        <ClientModal
          client={modal === "add" ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); fetchClients(); }}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 100,
                      display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={(e) => { if (e.target === e.currentTarget) setDeleteTarget(null); }}>
          <div style={{ background: "#fff", borderRadius: "10px", padding: "28px 32px",
                        maxWidth: "400px", width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <h3 style={{ margin: "0 0 10px", fontFamily: "Montserrat", fontSize: "16px", color: "#111" }}>
              Delete client?
            </h3>
            <p style={{ margin: "0 0 24px", fontSize: "13px", color: "#6B7280" }}>
              {deleteTarget.name
                ? `"${deleteTarget.name}" will be permanently removed from the carousel.`
                : "This client will be permanently removed from the carousel."}
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteTarget(null)}
                style={{ padding: "9px 18px", background: "#F3F4F6", border: "1px solid #E5E7EB",
                         borderRadius: "6px", fontSize: "13px", color: "#374151", cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={handleDelete}
                style={{ padding: "9px 18px", background: "#EF4444", border: "none",
                         borderRadius: "6px", fontSize: "13px", color: "#fff", cursor: "pointer" }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
